import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { siteUrl } from '../Constants';

const DEFAULT_TITLE = 'MyMed – Přípravné kurzy na přijímačky na medicínu';
const DEFAULT_DESCRIPTION =
    'Online přípravný kurz k přijímačkám na medicínu – fyzika, chemie a biologie. ' +
    'Připravíme tě na lékařskou fakultu v Praze, Brně i jinde.';
const DEFAULT_IMAGE = `${siteUrl}/images/common/logo.png`;

interface SeoProps {
    /** Page title; the brand suffix is appended automatically unless `title` already contains it. */
    title?: string;
    description?: string;
    /** Absolute URL of the social preview image. */
    image?: string;
    /** og:type — "website" (default) for pages, "article" for blog posts. */
    type?: 'website' | 'article';
    /** Keep the page out of the index (e.g. auth or 404 pages). */
    noindex?: boolean;
    /** Override the canonical path; defaults to the current route. */
    canonicalPath?: string;
    /** Extra head tags, e.g. a JSON-LD <script>. */
    children?: React.ReactNode;
}

// Single source of truth for per-page <head> tags (title, description, canonical,
// Open Graph, Twitter). Drop a <Seo .../> at the top of any public page.
export default function Seo({
    title,
    description = DEFAULT_DESCRIPTION,
    image = DEFAULT_IMAGE,
    type = 'website',
    noindex = false,
    canonicalPath,
    children,
}: SeoProps) {
    const { pathname } = useLocation();
    const fullTitle = !title
        ? DEFAULT_TITLE
        : title.includes('MyMed')
            ? title
            : `${title} | MyMed`;
    const canonical = siteUrl + (canonicalPath ?? pathname);

    return (
        <Helmet>
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            <link rel="canonical" href={canonical} />
            {noindex && <meta name="robots" content="noindex, follow" />}

            <meta property="og:type" content={type} />
            <meta property="og:url" content={canonical} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={image} />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />

            {children}
        </Helmet>
    );
}
